/**
 * Per-system environment looks for the Reach (M3 plan R3; spec §5 "a per-system seeded TSL nebula plus stars, baked
 * to a cubemap"): the region → nebula mapping with seeded variation, the star class → key light colour and
 * intensity, and the rig rotation that turns the key light toward the system's star while fill and rim keep their
 * authored angles to it. Pure maths and data, plus one allocation-free per-frame function (`aimRig`).
 * The sandbox never calls any of this, so its look stays exactly the authored environment.json one.
 */
import { Color, Quaternion, Vector3, type DirectionalLight } from 'three/webgpu'
import { createRng, range } from '../../core/rng'
import type { EnvironmentFile } from './data'
import type { NebulaConfig } from './sky/nebula'

export type SystemRegion = 'gate' | 'mid' | 'deep' | 'rim'

/** What one star system looks like from inside (R12 builds it from the galaxy's system record). */
export interface SystemLook {
  /** The system seed: nebula, band and star catalogue are regenerated from it. */
  seed: number
  region: SystemRegion
  /** The star class colour (`#rrggbb`, galaxy.json `stars[class].color`). */
  starColor: string
  /** The star class luminosity (G = 1). */
  luminosity: number
  /** The ancient-gate landmark (the home system only). */
  showGate: boolean
  /** M1's fixed-distance star and gas-giant impostors (the Reach draws the scaled-space pass instead). */
  showImpostors: boolean
}

export type SystemLooksConfig = EnvironmentFile['systemLooks']

/** Seeded offsets applied on top of a region's nebula (tests check them against `variation`). */
export interface LookVariation {
  hueDeg: number
  brightness: number
  warp: number
  dust: number
}

/** The seeded variation of a system: every term uniform within ± its `variation` bound. Deterministic. */
export function lookVariation(seed: number, v: SystemLooksConfig['variation']): LookVariation {
  const rng = createRng((seed ^ 0x3c6ef372) >>> 0)
  return {
    hueDeg: range(rng, -v.hueDeg, v.hueDeg),
    brightness: range(rng, -v.brightness, v.brightness),
    warp: range(rng, -v.warp, v.warp),
    dust: range(rng, -v.dust, v.dust),
  }
}

const scratchColor = new Color()
const scratchHsl = { h: 0, s: 0, l: 0 }

/** Rotates a `#rrggbb` hue by `deg` degrees (sRGB HSL), keeping saturation and lightness. */
export function rotateHue(hex: string, deg: number): string {
  scratchColor.set(hex).getHSL(scratchHsl)
  const h = (((scratchHsl.h + deg / 360) % 1) + 1) % 1
  scratchColor.setHSL(h, scratchHsl.s, scratchHsl.l)
  return `#${scratchColor.getHexString()}`
}

/**
 * The region's nebula (a partial over the authored `sky.nebula`), then the seed's variation. Rare (system
 * entry): allocates.
 */
export function nebulaForLook(env: EnvironmentFile, region: SystemRegion, seed: number): NebulaConfig {
  const looks = env.systemLooks
  const base: NebulaConfig = { ...env.sky.nebula, ...looks.regions[region] }
  const v = lookVariation(seed, looks.variation)
  return {
    ...base,
    hues: [rotateHue(base.hues[0], v.hueDeg), rotateHue(base.hues[1], v.hueDeg), rotateHue(base.hues[2], v.hueDeg)],
    brightness: base.brightness * (1 + v.brightness),
    warp: base.warp * (1 + v.warp),
    dust: Math.min(1, Math.max(0, base.dust + v.dust)),
  }
}

/** Piecewise-linear lookup of `[x, y]` points (x increasing), clamped to the end values. */
export function piecewiseLinear(table: readonly (readonly [number, number])[], x: number): number {
  const n = table.length
  if (x <= table[0][0]) return table[0][1]
  if (x >= table[n - 1][0]) return table[n - 1][1]
  for (let i = 1; i < n; i++) {
    const b = table[i]
    if (x <= b[0]) {
      const a = table[i - 1]
      return a[1] + ((b[1] - a[1]) * (x - a[0])) / (b[0] - a[0])
    }
  }
  return table[n - 1][1]
}

/** Key light intensity for a star: the authored key × `keyByLuminosity(luminosity)`. */
export function keyIntensityFor(env: EnvironmentFile, luminosity: number): number {
  return env.lights.key.intensity * piecewiseLinear(env.systemLooks.keyByLuminosity, luminosity)
}

/** Key light colour for a star: the authored key colour mixed toward the star colour by `starColorMix` (linear). */
export function keyColorFor(env: EnvironmentFile, starColor: string, out: Color): Color {
  const star = scratchColor.set(starColor)
  return out.set(env.lights.key.color).lerp(star, env.systemLooks.starColorMix)
}

// ---------------------------------------------------------------------------------------------------------------
// Rig aim (per frame, allocation-free)
// ---------------------------------------------------------------------------------------------------------------

/** The rig and the authored directions it rotates away from. `keyDist` is the key light's eye distance. */
export interface RigAim {
  readonly sun: DirectionalLight
  readonly fill: DirectionalLight
  readonly rim: DirectionalLight
  /** Authored unit directions (toward each light) from environment.json. */
  readonly key0: Vector3
  readonly fill0: Vector3
  readonly rim0: Vector3
  /** Distance of the key light's eye from its target, and of fill and rim from the origin. */
  readonly distance: number
  /** The rotation from the authored key direction to the current one (read by impostors that follow the rig). */
  readonly rotation: Quaternion
}

export function createRigAim(sun: DirectionalLight, fill: DirectionalLight, rim: DirectionalLight, env: EnvironmentFile, distance: number): RigAim {
  const unit = (d: readonly [number, number, number]): Vector3 => new Vector3(d[0], d[1], d[2]).normalize()
  return {
    sun,
    fill,
    rim,
    key0: unit(env.lights.key.direction),
    fill0: unit(env.lights.fill.direction),
    rim0: unit(env.lights.rim.direction),
    distance,
    rotation: new Quaternion(),
  }
}

const aimDir = new Vector3()
const aimTmp = new Vector3()

/**
 * Turns the key light toward the unit direction (x, y, z) (toward the star), keeping its target, and turns fill
 * and rim by the same rotation, so their angles to the key stay the authored ones. A zero vector is ignored.
 * The caller refits shadows and the atmosphere afterwards (stepEnvironment). Allocation-free.
 */
export function aimRig(r: RigAim, x: number, y: number, z: number): void {
  const l = Math.hypot(x, y, z)
  if (!(l > 1e-12)) return
  aimDir.set(x / l, y / l, z / l)
  r.rotation.setFromUnitVectors(r.key0, aimDir)
  const t = r.sun.target.position
  r.sun.position.set(t.x + aimDir.x * r.distance, t.y + aimDir.y * r.distance, t.z + aimDir.z * r.distance)
  aimTmp.copy(r.fill0).applyQuaternion(r.rotation).multiplyScalar(r.distance)
  r.fill.position.copy(aimTmp).add(r.fill.target.position)
  aimTmp.copy(r.rim0).applyQuaternion(r.rotation).multiplyScalar(r.distance)
  r.rim.position.copy(aimTmp).add(r.rim.target.position)
}
